import { useState } from "react";
import {
  AlertTriangle,
  BarChart3,
  Bot,
  Building2,
  CheckSquare,
  ClipboardList,
  Coins,
  Cog,
  FileText,
  Inbox,
  LayoutDashboard,
  LogOut,
  Map as MapIcon,
  MessageSquare,
  Plug,
  Settings,
  ShieldCheck,
  Sparkles,
  Truck,
  Users,
  Wrench,
} from "lucide-react";
import { AppShell, type NavGroup } from "@vseh/layouts";
import { Button } from "@vseh/ui";

import { Dashboard } from "./pages/Dashboard";
import { CommandCenter } from "./pages/CommandCenter";
import { ConnectorsPage } from "./pages/Connectors";
import { InboxPage } from "./pages/Inbox";
import { VehiclesPage } from "./pages/Vehicles";
import { DispatchMapPage } from "./pages/DispatchMap";
import { LoginPage } from "./pages/Login";
import { ListingsPage } from "./pages/Listings";
import { OrdersPage } from "./pages/Orders";
import { AlertsPage } from "./pages/Alerts";
import { CustomersPage } from "./pages/Customers";
import { ConversationsPage } from "./pages/Conversations";
import { AgentRunsPage } from "./pages/AgentRuns";
import { AuditLogPage } from "./pages/AuditLog";
import { CostDashboardPage } from "./pages/CostDashboard";
import { UsagePage }         from "./pages/Usage";
import { PlatformPage }      from "./pages/Platform";
import { PrivacyPage }       from "./pages/Privacy";
import { SettingsPage } from "./pages/Settings";
import { IntakePage } from "./pages/Intake";
import { ConsentRefreshBanner } from "./shared/components/ConsentRefreshBanner";
import { ErrorBoundary }        from "./shared/components/ErrorBoundary";
import { AuthProvider, useAuth } from "./shared/auth/AuthProvider";
import { ThemeProvider } from "./shared/theme/ThemeProvider";
import { ThemeToggle } from "./shared/theme/ThemeToggle";
import { LangSwitcher, LocaleSync, useT } from "./shared/i18n";


/** Build the nav with translated labels. Called per render so a
 *  language switch propagates without a full reload. The static
 *  parts (icons, hrefs, badges) are constant; only `label` reads
 *  from the dictionary. */
function buildNav(t: (k: string) => string): NavGroup[] {
  return [
    {
      items: [
        { id: "command",   label: t("nav.command"),    icon: Sparkles,
          href: "/command", badge: "dot", badgeTone: "ai" },
        { id: "inbox",     label: t("nav.inbox"),      icon: CheckSquare,
          href: "/inbox" },
        { id: "dashboard", label: t("nav.dashboard"),  icon: LayoutDashboard,
          href: "/" },
        { id: "listings",  label: t("nav.listings"),   icon: Truck,
          href: "/listings" },
        { id: "messages",  label: t("nav.messages"),   icon: MessageSquare,
          href: "/messages" },
        { id: "customers", label: t("nav.customers"),  icon: Users,
          href: "/customers" },
        { id: "fleet",     label: t("nav.fleet"),      icon: Wrench,
          href: "/fleet" },
        { id: "dispatch",  label: t("nav.dispatch"),   icon: MapIcon,
          href: "/dispatch", badge: "dot", badgeTone: "ai" },
        { id: "orders",    label: t("nav.orders"),     icon: ClipboardList,
          href: "/orders" },
        { id: "intake",    label: t("nav.intake") || "Воронка intake", icon: Inbox,
          href: "/intake", badge: "dot", badgeTone: "ai" },
        { id: "alerts",    label: t("nav.alerts"),     icon: AlertTriangle,
          href: "/alerts" },
      ],
    },
    {
      items: [
        { id: "agent-runs", label: t("nav.agent_runs"), icon: Bot,       href: "/agent-runs" },
        { id: "connectors", label: t("nav.connectors"), icon: Plug,      href: "/connectors" },
        { id: "cost",       label: t("nav.cost"),       icon: Coins,     href: "/cost" },
        { id: "usage",      label: t("nav.usage"),      icon: Coins,     href: "/usage" },
        { id: "platform",   label: t("nav.platform"),   icon: Building2, href: "/platform" },
        { id: "audit",      label: t("nav.audit"),      icon: FileText,  href: "/audit" },
        { id: "analytics",  label: t("nav.analytics"),  icon: BarChart3, href: "/analytics" },
      ],
    },
    {
      items: [
        { id: "settings", label: t("nav.settings"), icon: Settings, href: "/settings" },
        { id: "privacy",  label: t("nav.privacy"),  icon: ShieldCheck, href: "/privacy" },
      ],
    },
  ];
}


export function App(): JSX.Element {
  return (
    <ThemeProvider>
      <AuthProvider>
        {/* Sprint EA-4 — hydrates lang from users.locale on
            login and persists subsequent changes via
            PATCH /me/locale. Pure side-effect node, no DOM. */}
        <LocaleSync />
        <AuthGate />
      </AuthProvider>
    </ThemeProvider>
  );
}


/**
 * Gates the whole app behind a successful auth state.
 * Until the user logs in, they see only the LoginPage.
 */
function AuthGate(): JSX.Element {
  const { auth } = useAuth();
  if (!auth) return <LoginPage />;
  return <AuthedApp />;
}


function AuthedApp(): JSX.Element {
  const { auth, logout } = useAuth();
  const t = useT();
  const [activePath, setActivePath] = useState("/command");
  const [aiPanelOpen, setAiPanelOpen] = useState(false);

  // Filter nav by scope: don't show items the user can't use. Empty scope
  // list (legacy users) gets the full nav for backward compat.
  const navForUser = filterNavByScopes(buildNav(t), auth?.claims.scopes ?? []);

  return (
    <AppShell
      brand={
        <span className="flex items-center gap-2">
          <span
            aria-hidden="true"
            className="grid size-6 place-items-center rounded-md bg-primary-900 text-white"
          >
            <Cog className="size-3.5" />
          </span>
          <span>VSEH AI OS</span>
        </span>
      }
      nav={navForUser}
      activePath={activePath}
      onNavigate={(item) => setActivePath(item.href)}
      aiPanel={<AiPanelStub />}
      aiPanelOpen={aiPanelOpen}
      onAiPanelToggle={setAiPanelOpen}
      topBarTrailing={
        <div className="flex items-center gap-2">
          <LangSwitcher />
          <ThemeToggle />
          <Button
            variant="ghost"
            size="xs"
            leftIcon={<LogOut className="size-3.5" />}
            onClick={logout}
            title={t("common.logout.title", { user: auth?.claims.sub ?? "" })}
          >
            {t("common.logout")}
          </Button>
        </div>
      }
    >
      {/* Sprint BO — re-consent banner sits above whatever page is
          active so a regulator-mandated re-acceptance is visible
          without trapping the operator mid-task. The banner self-
          hides when the user accepts (or never had a refresh due). */}
      <ConsentRefreshBanner onNavigate={setActivePath} />
      {/* Sprint BQ — per-route error boundary. Keyed on `activePath`
          so navigating away from a broken page resets the boundary
          state cleanly; without the key, a stuck error would
          shadow every subsequent route. */}
      <ErrorBoundary key={activePath} scopeLabel={activePath}>
        <PageContent path={activePath} />
      </ErrorBoundary>
    </AppShell>
  );
}


/** Map nav item id → required scope. Items absent here are always visible. */
const NAV_SCOPE_REQUIREMENTS: Record<string, string> = {
  command:     "agent.execute",
  inbox:       "approval.read",
  listings:    "listing.read",
  orders:      "listing.read",
  alerts:      "vehicle.read",
  messages:    "conversation.read",
  customers:   "customer.read",
  "agent-runs": "agent.read",
  connectors:  "admin.connectors",
  cost:        "analytics.read",
  audit:       "audit.read",
  analytics:   "analytics.read",
  fleet:       "vehicle.read",
  dispatch:    "vehicle.read",
  settings:    "admin.connectors",
  usage:       "analytics.read",
  platform:    "admin.tenants",
};

function filterNavByScopes(groups: NavGroup[], scopes: string[]): NavGroup[] {
  // Empty scopes = legacy backstop; show everything.
  if (scopes.length === 0) return groups;
  return groups
    .map((g) => ({
      ...g,
      items: g.items.filter((item) => {
        const required = NAV_SCOPE_REQUIREMENTS[item.id];
        return !required || scopes.includes(required);
      }),
    }))
    .filter((g) => g.items.length > 0);
}


function PageContent({ path }: { path: string }): JSX.Element {
  switch (path) {
    case "/command":     return <CommandCenter />;
    case "/inbox":       return <InboxPage />;
    case "/":            return <Dashboard />;
    case "/listings":    return <ListingsPage />;
    case "/orders":      return <OrdersPage />;
    case "/intake":      return <IntakePage />;
    case "/alerts":      return <AlertsPage />;
    case "/customers":   return <CustomersPage />;
    case "/messages":    return <ConversationsPage />;
    case "/agent-runs":  return <AgentRunsPage />;
    case "/connectors":  return <ConnectorsPage />;
    case "/cost":        return <CostDashboardPage />;
    case "/usage":       return <UsagePage />;
    case "/platform":    return <PlatformPage />;
    case "/privacy":     return <PrivacyPage />;
    case "/audit":       return <AuditLogPage />;
    case "/fleet":       return <VehiclesPage />;
    case "/dispatch":    return <DispatchMapPage />;
    case "/settings":    return <SettingsPage />;
    default:             return <NotImplemented path={path} />;
  }
}


function NotImplemented({ path }: { path: string }): JSX.Element {
  const t = useT();
  return (
    <div className="grid place-items-center rounded-lg border border-dashed border-base-300 bg-base-50 px-4 py-16 text-center">
      <div>
        <div className="font-display text-xl font-semibold text-base-900">
          {t("shell.notimpl.title", { path })}
        </div>
        <div className="mt-2 text-sm text-base-600">
          {t("shell.notimpl.body")}
        </div>
      </div>
    </div>
  );
}


function AiPanelStub(): JSX.Element {
  const t = useT();
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-base-200 p-4">
        <div className="font-display text-lg font-semibold">{t("shell.ai.title")}</div>
        <div className="text-xs text-base-500">
          {t("shell.ai.hint")}
        </div>
      </div>
      <div className="flex-1 p-4 text-sm text-base-500">
        {t("shell.ai.realtime")}{" "}
        {t("shell.ai.usefor")}
      </div>
    </div>
  );
}
